import React from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import BrandRow from './BrandRow';
import { columnHeaderCells, columnsFor, groupHeaderCells } from './listColumns';
import TouchTooltip from '../../common/TouchTooltip';
import { BALANCE_TOOLTIP } from './constants';

const ShowMore = ({ hasMore, onShowMore, loadingMore, isWide }) =>
  hasMore ? (
    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Button variant="outlined" onClick={onShowMore} disabled={loadingMore} sx={{ minHeight: isWide ? 36 : 44 }}>
        {loadingMore ? 'Loading…' : 'Show more'}
      </Button>
    </Box>
  ) : null;

/** Carried brands (spec §1.3): the same Received / Sent columns as Products, in the same order
 * (Daniel 2026-10-05). Wide = table with a two-level sortable header; narrow = cards. */
const BrandList = ({
  rows,
  isWide,
  loading,
  loadingMore,
  hasMore,
  onShowMore,
  sort,
  onSort,
  isExpanded,
  onToggle,
  onOpen,
  emptyState,
  detailParams,
  window,
}) => {
  if (loading && rows.length === 0) {
    return [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={isWide ? 56 : 110} sx={{ borderRadius: 1, mb: 1 }} />);
  }
  if (!loading && rows.length === 0) return emptyState;
  const columns = columnsFor('both', sort);
  const rowProps = (row) => ({ row, isWide, expanded: isExpanded(row.brand), onToggle, onOpen, detailParams, window, columns });
  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {isWide ? (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Brands">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ width: 40, px: 0.5 }} />
                <TableCell rowSpan={2} sx={{ minWidth: 240 }}>
                  Brand
                </TableCell>
                <TableCell rowSpan={2} sx={{ minWidth: 170 }}>
                  <TouchTooltip title={BALANCE_TOOLTIP}>
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                      Click balance
                      <Typography variant="caption" color="text.secondary" component="div">
                        ← received · sent →
                      </Typography>
                    </Box>
                  </TouchTooltip>
                </TableCell>
                {groupHeaderCells(columns)}
              </TableRow>
              <TableRow>{columnHeaderCells(columns, onSort)}</TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <BrandRow key={row.brand} {...rowProps(row)} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          {rows.map((row) => (
            <BrandRow key={row.brand} {...rowProps(row)} />
          ))}
        </Box>
      )}
      <ShowMore hasMore={hasMore} onShowMore={onShowMore} loadingMore={loadingMore} isWide={isWide} />
    </Box>
  );
};

export default BrandList;
